// routes/SpaceShell.jsx — 현재 Space를 벗어나지 않는 공동 작업 셸.
//
// 데스크톱에서는 Space 경계와 네 개의 작업 표면(공간·대화·수신함·Task)을
// 명시적으로 보인다. 좁힌 레일은 보조 상태이며, 그 경우에도 aria-label을 유지한다.
//
// **D-4 가 여기서 죽는다.** `space-home` 이 `ready` 가 아니면 셸을 그리지 않는다.
// 셸을 먼저 그리고 나중에 실패를 얹는 경로가 코드에 없다.

import { useEffect, useState } from 'preact/hooks';
import { Link, Route, Switch, useLocation, useRoute } from 'wouter-preact';
import { read } from '../api/client.js';
import { WriteBoundary } from '../api/write-boundary.js';
import { useResource } from '../api/resource.js';
import { useSession } from '../session/session.jsx';
import { Avatar, Button, ButtonLink, Icon, Select } from '../ui/primitives.jsx';
import { ErrorView, Loading } from '../ui/states.jsx';
import { ThreadPane, NewThreadPane } from './ThreadPane.jsx';
import { OverviewPane } from './OverviewPane.jsx';
import { InboxPane } from './InboxPane.jsx';
import { TasksPane } from './TasksPane.jsx';
import { TaskDetailPane } from './TaskDetailPane.jsx';
import { TaskReentryPane } from './TaskReentryPane.jsx';
import { SpaceContextPane } from './SpaceContextPane.jsx';
import { ThreadsPane } from './ThreadsPane.jsx';
import { AiNodesPane } from './AiNodesPane.jsx';
import { ResourcesPane } from './ResourcesPane.jsx';

export function SpaceShell() {
  const [rootMatch, rootParams] = useRoute('/s/:spaceId');
  const [nestedMatch, nestedParams] = useRoute('/s/:spaceId/*');
  const match = rootMatch || nestedMatch;
  const spaceId = (rootParams ?? nestedParams)?.spaceId ?? null;
  const session = useSession();
  const [, navigate] = useLocation();

  const home = useResource(spaceId ? `space:${spaceId}:home` : null, (signal) =>
    read('space-home', { nodeId: session.nodeId, query: { spaceId }, signal }),
    { quiet: true, identity: session.nodeId },
  );

  if (!match || !spaceId) return null;

  // 셸 자체가 space-home 에 의존한다. 실패하면 셸이 아니라 실패만 보인다.
  if (home.status === 'error') {
    return (
      <main class="boot">
        <ErrorView error={home.error} onRetry={home.reload} onJoin={() => navigate('/')} />
        <div class="row">
          <Button size="sm" tone="ghost" onClick={() => navigate('/')}>
            참여 중인 공간으로
          </Button>
        </div>
      </main>
    );
  }
  if (home.status === 'loading') {
    return (
      <main class="boot">
        <Loading label="공간을 여는 중" />
      </main>
    );
  }

  const space = home.data.space;
  const archived = space?.state === 'archived';

  return (
    <WriteBoundary resources={[home]}><div class="shell" data-theme="dark">
      <a class="skip-link btn btn--neutral btn--sm" href="#space-main">본문으로 건너뛰기</a>
      <div class="shell__frame">
        <IconRail spaceId={spaceId} space={space} session={session} />

        <main id="space-main" class="shell__content" tabindex="-1">
          <Switch>
            <Route path="/s/:spaceId">
              <OverviewPane spaceId={spaceId} home={home.data} />
            </Route>
            <Route path="/s/:spaceId/overview">
              <OverviewPane spaceId={spaceId} home={home.data} />
            </Route>
            <Route path="/s/:spaceId/threads">
              <ThreadsPane spaceId={spaceId} archived={archived} />
            </Route>
            <Route path="/s/:spaceId/context">
              <SpaceContextPane spaceId={spaceId} />
            </Route>
            <Route path="/s/:spaceId/resources">
              <ResourcesPane spaceId={spaceId} archived={archived} />
            </Route>
            <Route path="/s/:spaceId/t/new">
              <ThreadsPane spaceId={spaceId} archived={archived} />
              <NewThreadPane spaceId={spaceId} archived={archived} />
            </Route>
            <Route path="/s/:spaceId/t/:threadId">
              {(p) => <ThreadPane spaceId={spaceId} threadId={p.threadId} archived={archived} members={home.data.members ?? []} />}
            </Route>
            <Route path="/s/:spaceId/inbox">
              <InboxPane key={`${spaceId}:${session.nodeId}`} spaceId={spaceId} archived={archived} home={home.data} />
            </Route>
            <Route path="/s/:spaceId/tasks">
              <TasksPane spaceId={spaceId} members={home.data.members ?? []} />
            </Route>
            <Route path="/s/:spaceId/nodes">
              <AiNodesPane spaceId={spaceId} members={home.data.members ?? []} />
            </Route>
            <Route path="/s/:spaceId/task/:taskId/reentry">
              {(p) => <TaskReentryPane spaceId={spaceId} taskId={p.taskId} />}
            </Route>
            <Route path="/s/:spaceId/task/:taskId">
              {(p) => <TaskDetailPane spaceId={spaceId} taskId={p.taskId} archived={archived} home={home.data} />}
            </Route>
            <Route>
              <UnknownSpaceRoute spaceId={spaceId} />
            </Route>
          </Switch>
        </main>
      </div>
    </div></WriteBoundary>
  );
}

/**
 * 계약이 없는 Resource·Bridge·권한 화면을 빈 제품 화면으로 꾸미지 않는다.
 * 이 경계에서는 존재하는 네 작업면으로 돌아가는 길만 준다.
 */
function UnknownSpaceRoute({ spaceId }) {
  return (
    <section class="work">
      <div class="work__body">
        <ErrorView error={{ status: 404, code: 'route_not_found' }} />
        <div class="row">
          <ButtonLink size="sm" href={`/s/${spaceId}`}>대화로 돌아가기</ButtonLink>
        </div>
      </div>
    </section>
  );
}

const RAIL_ITEMS = [
  // Space root는 사람이 다시 방향을 잡는 홈이다. Thread를 루트에 끼워 넣거나
  // 최신 대화로 자동 전환하지 않는다.
  { key: 'overview', object: 'node', icon: 'space', label: '개요', path: '', also: '/overview' },
  { key: 'context', object: 'task', icon: 'task', label: '공동 맥락', path: 'context' },
  { key: 'threads', object: 'thread', icon: 'thread', label: '대화', path: 'threads', also: '/t/' },
  { key: 'inbox', object: 'inbox', icon: 'inbox', label: '수신함', path: 'inbox' },
  { key: 'tasks', object: 'task', icon: 'task', label: 'Task', path: 'tasks', also: '/task/' },
  { key: 'resources', object: 'resource', icon: 'resource', label: '자료', path: 'resources' },
  { key: 'nodes', object: 'node', icon: 'node', label: 'AI Node', path: 'nodes' },
];

function IconRail({ spaceId, space, session }) {
  const [location] = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [location]);

  const isCurrent = (item) => {
    const base = item.path ? `/s/${spaceId}/${item.path}` : `/s/${spaceId}`;
    // 기본 대화면은 Space 루트와 실제 Thread 상세뿐이다. 여기서 모든 하위 URL을
    // 포함시키면 Inbox·Task를 보고 있어도 “대화”가 선택된 것으로 잘못 읽힌다.
    if (!item.path) {
      return location === base || (item.also && location.startsWith(`/s/${spaceId}${item.also}`))
        ? 'page'
        : undefined;
    }
    if (location === base || location.startsWith(`${base}/`)) return 'page';
    if (item.also && location.startsWith(`/s/${spaceId}${item.also}`)) return 'page';
    return undefined;
  };

  return (
    <nav class="irail irail--wide" aria-label="현재 Space 이동">
      <Link
        href={`/s/${spaceId}/overview`}
        class="irail__brand"
        title={`${space?.name ?? '공간'} 개요`}
      >
        <span class="irail__brandchip"><Icon name="space" width="18" height="18" /></span>
        <span class="irail__brand-copy">
          <span class="irail__eyebrow">현재 Space</span>
          <span class="irail__text t-body-strong">{space?.name ?? '공간'}</span>
        </span>
      </Link>

      <p class="irail__section">작업 공간</p>

      {RAIL_ITEMS.map((item) => (
        <Link
          key={item.key}
          href={item.path ? `/s/${spaceId}/${item.path}` : `/s/${spaceId}`}
          class={`irail__item irail__item--${item.object}`}
          aria-current={isCurrent(item)}
          title={item.hint ?? item.label}
        >
          <Icon name={item.icon} width="18" height="18" />
          <span class="irail__text">{item.label}</span>
        </Link>
      ))}

      <button
        type="button"
        class="irail__me"
        aria-label="내 자리 — 이름, 공간 전환, 로그아웃"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <Avatar label={session.node.displayName} />
        <span class="irail__text">{session.node.displayName}</span>
        <Icon name="chevron-down" width="16" height="16" />
      </button>

      {menuOpen ? (
        <div class="irail__menu" role="dialog" aria-label="내 자리">
          <div class="irail__menu-name">
            <Avatar label={session.node.displayName} />
            <div class="grow">
              <p class="t-body-strong">{session.node.displayName}</p>
              <p class="t-meta">{session.node.kind === 'hybrid' ? '사람+AI' : '사람'} Node</p>
            </div>
          </div>
          <SpaceSwitcher current={space} spaces={session.spaces} />
          <Button size="sm" tone="ghost" onClick={session.logout}>로그아웃</Button>
        </div>
      ) : null}
    </nav>
  );
}

function SpaceSwitcher({ current, spaces }) {
  const [, navigate] = useLocation();
  return (
    <label class="stack stack--tight">
      <span class="t-meta">공간 전환</span>
      <Select
        size="sm"
        value={current?.spaceId ?? ''}
        onChange={(e) => {
          const value = e.currentTarget.value;
          if (value === '__entry__') navigate('/');
          else navigate(`/s/${value}`);
        }}
      >
        {spaces
          .filter((s) => s.state === 'active')
          .map((s) => (
            <option key={s.spaceId} value={s.spaceId}>
              {s.name ?? '이름 없음'}
            </option>
          ))}
        <option value="__entry__">다른 공간 찾기 / 새로 만들기 →</option>
      </Select>
    </label>
  );
}
